import decode from "jwt-decode";
import axios from "axios";
import { takeCognitoLogoutUrl } from "./sso/cognitoAuth";
async function login(username, password) {
  // Clear filters from sessionStorage
  removeFiltersFromSessionStorage();
  // Get a token
  var postData = {
    username: username,
    password: password,
  };
  var config = {
    "Content-Type": "application/json",
  };
  return await axios
    .post("api/tokens/access", postData, config)
    .then((res) => {
      setToken(res.data.token);
      setItem(
        res.data.isForceChangePassword,
        res.data.hasAdmincenterVtPaymentsPlc
      );
      setIdleTime(res.data.maxIdleTime);
      return res;
    })
    .catch((err) => {
      return err.response;
    });
}

// Exchange a Cognito ID token (from the SSO redirect) for a Proview JWT. The result is stored exactly like a
// password login, so everything downstream (Bearer header, role checks, session timeout) is unchanged.
async function loginWithSso(idToken) {
  removeFiltersFromSessionStorage();
  return await axios
    .post(
      "/api/tokens/sso",
      { idToken: idToken },
      { "Content-Type": "application/json" }
    )
    .then((res) => {
      setToken(res.data.token);
      setItem(
        res.data.isForceChangePassword,
        res.data.hasAdmincenterVtPaymentsPlc
      );
      setIdleTime(res.data.maxIdleTime);
      return res;
    })
    .catch((err) => {
      return err.response;
    });
}

async function signup(username, password) {
  // Get a token
  var postData = {
    username: username,
    password: password,
  };
  var config = {
    "Content-Type": "application/json",
  };
  return await axios
    .post("api/signup", postData, config)
    .then((res) => {
      return res;
    })
    .catch((err) => {
      return err.response;
    });
}

async function change(curpassword, newpassword, confirmpassword) {
  // Get a token
  var postData = {
    curpassword: curpassword,
    newpassword: newpassword,
    confirmpassword: confirmpassword,
  };
  var config = {
    headers: {
      Authorization: "Bearer " + getToken(),
      "Content-Type": "application/json",
    },
  };
  return await axios
    .post("/api/change", postData, config)
    .then((res) => {
      setToken(res.data.token);
      setItem(
        res.data.isForceChangePassword,
        res.data.hasAdmincenterVtPaymentsPlc
      );
      return res;
    })
    .catch((err) => {
      return err.response;
    });
}

function loggedIn() {
  // Checks if there is a saved token and it's still valid
  const token = getToken();
  return !!token && !isTokenExpired(token); // handwaiving here
}

function isTokenExpired(token) {
  try {
    const decoded = decode(token);
    return decoded.exp < Date.now() / 1000;
  } catch (err) {
    return false;
  }
}

function setToken(idToken) {
  // Saves user token to localStorage
  sessionStorage.setItem("id_token", idToken);
}

function getToken() {
  // Retrieves the user token from localStorage
  return sessionStorage.getItem("id_token");
}

function setItem(isForceChangePassword, hasAdmincenterVtPaymentsPlc) {
  sessionStorage.setItem("is_force_change_password", isForceChangePassword);
  sessionStorage.setItem(
    "has_admincenter_vt_payments_plc",
    hasAdmincenterVtPaymentsPlc
  );
}

function setIdleTime(maxIdleTime) {
  sessionStorage.setItem("max_idle_time", maxIdleTime);
}

function getIdleTime() {
  return sessionStorage.getItem("max_idle_time");
}

function isForceChangePassword() {
  return sessionStorage.getItem("is_force_change_password");
}

function canViewUserManagement() {
  let token = getToken();
  return token ? decode(token).hasCygUserManagement === true : null;
}

function logout() {
  // Clear user token and profile data from localStorage
  var config = {
    headers: {
      Authorization: "Bearer " + getToken(),
      "Content-Type": "application/json",
    },
  };
  sessionStorage.removeItem("id_token");
  sessionStorage.removeItem("max_idle_time");
  sessionStorage.removeItem("is_force_change_password");
  sessionStorage.removeItem("has_admincenter_vt_payments_plc");
  removeFiltersFromSessionStorage();
  // Only set for users who signed in through SSO; password users stay in the app.
  const cognitoLogoutUrl = takeCognitoLogoutUrl();
  return axios
    .post("/api/token/revoke", null, config)
    .then((res) => {
      return res;
    })
    .catch((err) => {
      return err.response;
    })
    .finally(() => {
      // End the Cognito Hosted UI session too, otherwise the next "Sign in" would skip the IdP prompt.
      if (cognitoLogoutUrl) {
        window.location.assign(cognitoLogoutUrl);
      }
    });
}

function getProfile() {
  let token = getToken();
  return token ? decode(token) : null;
}

// check if user has transaction modify privilege
function canModify() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_TRANSACTION_MODIFY")
    : null;
}

// check if user has transaction amount modify privilege
function canAmountModify() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_TRANSACTION_AMOUNT_MODIFY")
    : null;
}

// check if user has finance privilege
function canFinance() {
  let token = getToken();
  return token ? decode(token).authorities.includes("ROLE_FINANCE") : null;
}

// check if user has transaction manifest privilege
function canTransactionManifest() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_TRANSACTION_MANIFEST")
    : null;
}

// check if user has finance override privilege
function canFinanceOverride() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_FINANCE_OVERRIDE")
    : null;
}

// check if user has finance override privilege
function canAdmin() {
  let token = getToken();
  return token ? decode(token).authorities.includes("GROUP_GPS_ADMIN") : null;
}

//check if user has transaction deny privilege
function canTransactionDeny() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_TRANSACTION_DENY")
    : null;
}

//check if user has agency edit group role
function canAgencyEdit() {
  let token = getToken();
  return token ? decode(token).authorities.includes("GROUP_AGENCY_EDIT") : null;
}

//check if user has agency admin group role
function canAgencyAdmin() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("GROUP_AGENCY_ADMIN_USER")
    : null;
}

//check if user has resend plc
function canPlcResend() {
  let token = getToken();
  return token ? decode(token).authorities.includes("ROLE_PLC_RESEND") : null;
}
//check if user has plc information change
function canPlcInformationChange() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_PLC_INFORMATION_CHANGE")
    : null;
}
//check if user has service fee override
function canServiceFeeOverride() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_SERVICE_FEE_OVERRIDE")
    : null;
}
//check if user has allow CSRDNE
function canSendCSRDNE() {
  let token = getToken();
  return token ? decode(token).authorities.includes("ROLE_ALLOW_CSRDNE") : null;
}
//check if user has finance group role
function canFinanceUser() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("GROUP_FINANCE_USER")
    : null;
}
//check if user has notification_details role
function canProviewNotificationDetails() {
  let token = getToken();
  return token
    ? decode(token).authorities.includes("ROLE_PROVIEW_NOTIFICATION_DETAILS")
    : null;
}

// call center agent groups; each is matched by name prefix (GROUP_CALL_CENTER_AGENTS*)
const CALL_CENTER_PREFIXES = [
  "GROUP_CALL_CENTER_AGENTS",
  "GROUP_CALL_CENTER_AGENTS_NEW",
  "GROUP_CALL_CENTER_AGENTS_SUPERVISORS",
];

//check if user has gps sales group role
function canSales() {
  let token = getToken();
  return token ? decode(token).authorities.includes("GROUP_GPS_SALES") : null;
}
//check if user is in a call center agents group
function canCallCenter() {
  let token = getToken();
  return token
    ? String(decode(token).authorities)
        .split(",")
        .some((authority) =>
          CALL_CENTER_PREFIXES.some((prefix) => authority.startsWith(prefix))
        )
    : null;
}

// check if user may see the transaction Source column (internal AllPaid users
// only: GPS admin, GPS finance user, GPS sales, or a call center agents group).
// Keep in sync with SourceVisibility.java
function canViewSource() {
  return (
    canAdmin() === true ||
    canFinanceUser() === true ||
    canSales() === true ||
    canCallCenter() === true
  );
}

function removeFiltersFromSessionStorage() {
  sessionStorage.removeItem("transSummaryFilter");
  sessionStorage.removeItem("transManifestFilter");
  sessionStorage.removeItem("recurringFilter");
  sessionStorage.removeItem("migrationFilter");
  sessionStorage.removeItem("transPartOfRPFilter");
}

export {
  login,
  loginWithSso,
  signup,
  change,
  loggedIn,
  isTokenExpired,
  setToken,
  logout,
  getToken,
  getIdleTime,
  getProfile,
  canModify,
  canAmountModify,
  canFinance,
  canTransactionManifest,
  canFinanceOverride,
  canAdmin,
  canViewSource,
  isForceChangePassword,
  canTransactionDeny,
  canAgencyEdit,
  canAgencyAdmin,
  canPlcResend,
  canPlcInformationChange,
  canServiceFeeOverride,
  canSendCSRDNE,
  canFinanceUser,
  canSales,
  canCallCenter,
  canProviewNotificationDetails,
  canViewUserManagement,
};
